import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  AudAtividade, AudCriterio, AudAuditoria, AudFaixa,
} from "@shared/schema";

// ─── Tipos de leitura (espelham o shape montado em server/storage/auditoria.ts) ──

export type AtividadeComCriterios = AudAtividade & { criterios: AudCriterio[] };

export type ResponsavelResumo = { funcionarioId: string; nome: string };

export type TipoAuditoria = "orientativa" | "fechamento";

export type AuditoriaResumo = {
  id: string;
  setorId: string;
  setorNome: string | null;
  data: string;
  auditorNome: string;
  /** Média simples das notas de critério, calculada e congelada no fechamento — nunca digitada. */
  notaFinal: string | null;
  status: "rascunho" | "fechada";
  /** orientativa = direciona o time, não vai para o Financeiro. fechamento = nota da competência (máx. 1/setor/mês). */
  tipo: TipoAuditoria;
  qtdAtividades: number;
  qtdCriteriosAvaliados: number;
  responsaveis: ResponsavelResumo[];
};

/** Resumo da auditoria de fechamento já existente, devolvido no 409 FECHAMENTO_JA_EXISTE. */
export type AuditoriaExistente = {
  id: string;
  setorId: string;
  setorNome: string | null;
  data: string;
  notaFinal: string | null;
  status: "rascunho" | "fechada";
};

/** Erro de negócio do módulo — extrai `code`/`auditoriaExistente` do corpo JSON lançado por apiRequest. */
export function parseErroAuditoria(e: unknown, fallback = "Tente novamente."): {
  message: string; code?: string; auditoriaExistente?: AuditoriaExistente;
} {
  const bruto = e instanceof Error ? e.message : String(e ?? "");
  const m = /^\d{3}:\s*([\s\S]*)$/.exec(bruto);
  const corpo = m ? m[1] : bruto;
  try {
    const j = JSON.parse(corpo);
    if (j && typeof j.error === "string") {
      return { message: j.error, code: j.code, auditoriaExistente: j.auditoriaExistente };
    }
  } catch {
    /* corpo não é JSON — usa o texto cru */
  }
  return { message: corpo || fallback };
}

/** Quem recebe nota é o CRITÉRIO — a atividade só agrupa, sem peso. */
export type AuditoriaItemDetalhe = {
  id: string;
  audAtividadeId: string;
  atividadeNome: string;
  grupo: "trato" | "especializada";
  observacao: string | null;
  criteriosCatalogo: AudCriterio[];
  notas: Array<{ audCriterioId: string; nota: string }>;
};

export type AuditoriaDetalhe = AudAuditoria & {
  setorNome: string | null;
  responsavelIds: string[];
  responsaveis: ResponsavelResumo[];
  itens: AuditoriaItemDetalhe[];
};

export type RelatorioFuncionario = {
  funcionarioId: string;
  nome: string;
  avaliacoes: number;
  notaMedia: number | null;
};

// ─── Listas de apoio ──────────────────────────────────────────────────────────
// Servidas pelo próprio módulo: as telas de Auditoria não podem depender de
// /api/equipe/* (gate do módulo Equipe) nem de hooks de outro módulo.

export type SetorAuditoria = { id: string; nome: string; areaHectares: string | null; ativo: boolean };
export type FuncionarioAuditoria = { id: string; nome: string; apelido: string | null };

export function useSetoresAuditoria() {
  return useQuery<SetorAuditoria[]>({
    queryKey: ["/api/auditoria/setores"],
    queryFn: () => fetchJson<SetorAuditoria[]>("/api/auditoria/setores"),
  });
}

export function useFuncionariosAuditoria() {
  return useQuery<FuncionarioAuditoria[]>({
    queryKey: ["/api/auditoria/funcionarios"],
    queryFn: () => fetchJson<FuncionarioAuditoria[]>("/api/auditoria/funcionarios"),
  });
}

/** Encarregados ATIVOS do setor (funcionario_setor, fim IS NULL) — pré-preenche os responsáveis. */
export function useSugestaoResponsaveisAuditoria(setorId: string | undefined) {
  return useQuery<FuncionarioAuditoria[]>({
    queryKey: ["/api/auditoria/setores", setorId, "responsaveis-sugeridos"],
    queryFn: () => fetchJson<FuncionarioAuditoria[]>(`/api/auditoria/setores/${setorId}/responsaveis-sugeridos`),
    enabled: !!setorId,
  });
}

// ─── Catálogo ─────────────────────────────────────────────────────────────────

export type AtividadeDisponivel = {
  atividadeId: string | null;
  audAtividadeId: string | null;
  nome: string;
  auditar: boolean;
  /** Ordem de exibição (setas ↑↓ em Critérios auditoria); null = ainda sem aud_atividade. */
  ordem: number | null;
  criterios: AudCriterio[];
};

/** LEFT JOIN do cadastro de atividades do Equipe com aud_atividade — tela "Atividades e critérios". */
export function useAtividadesDisponiveisAuditoria() {
  return useQuery<AtividadeDisponivel[]>({
    queryKey: ["/api/auditoria/atividades-disponiveis"],
    queryFn: () => fetchJson<AtividadeDisponivel[]>("/api/auditoria/atividades-disponiveis"),
  });
}

export function useAtividadesAuditoria(includeInativas = false) {
  const url = includeInativas ? "/api/auditoria/atividades?includeInativas=true" : "/api/auditoria/atividades";
  return useQuery<AtividadeComCriterios[]>({
    queryKey: ["/api/auditoria/atividades", includeInativas],
    queryFn: () => fetchJson<AtividadeComCriterios[]>(url),
  });
}

/** Cria aud_atividade — liga uma atividade do cadastro (atividadeId preenchido) ou cria "só de auditoria" (atividadeId nulo). */
export function useCreateAtividadeAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { nome: string; atividadeId?: string | null; grupo?: "trato" | "especializada"; ativo?: boolean }) =>
      apiRequest("POST", "/api/auditoria/atividades", data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades"] });
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades-disponiveis"] });
    },
  });
}

export function useUpdateAtividadeAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/auditoria/atividades/${id}`, data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades"] });
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades-disponiveis"] });
    },
  });
}

export function useCreateCriterioAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ atividadeId, data }: { atividadeId: string; data: Record<string, unknown> }) =>
      apiRequest("POST", `/api/auditoria/atividades/${atividadeId}/criterios`, data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades"] });
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades-disponiveis"] });
    },
  });
}

export function useUpdateCriterioAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/auditoria/criterios/${id}`, data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades"] });
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades-disponiveis"] });
    },
  });
}

/** Grava a ordem de exibição inteira de uma vez — setas ↑↓ em Critérios auditoria. */
export function useUpdateOrdemAtividadesAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ordens: Array<{ id: string; ordem: number }>) =>
      apiRequest("PUT", "/api/auditoria/atividades/ordem", { ordens }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades"] });
      qc.invalidateQueries({ queryKey: ["/api/auditoria/atividades-disponiveis"] });
    },
  });
}

// ─── Auditorias ───────────────────────────────────────────────────────────────

export function useAuditorias(filtros: { setorId?: string; de?: string; ate?: string } = {}) {
  const qs = new URLSearchParams(Object.entries(filtros).filter(([, v]) => !!v) as [string, string][]).toString();
  const url = qs ? `/api/auditoria/auditorias?${qs}` : "/api/auditoria/auditorias";
  return useQuery<AuditoriaResumo[]>({
    queryKey: ["/api/auditoria/auditorias", qs],
    queryFn: () => fetchJson<AuditoriaResumo[]>(url),
  });
}

export function useAuditoria(id: string | undefined) {
  return useQuery<AuditoriaDetalhe>({
    queryKey: ["/api/auditoria/auditorias", id],
    queryFn: () => fetchJson<AuditoriaDetalhe>(`/api/auditoria/auditorias/${id}`),
    enabled: !!id,
  });
}

export function useCreateAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      setorId: string; data: string; auditorNome: string; tipo?: TipoAuditoria;
      responsavelIds: string[]; atividadeIds: string[];
    }) => apiRequest("POST", "/api/auditoria/auditorias", data).then(r => r.json() as Promise<AuditoriaDetalhe>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/auditorias"] }),
  });
}

/** `data` também aceita `responsavelIds` (substitui a lista inteira) e `tipo` (orientativa/fechamento). */
export function usePatchAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/auditoria/auditorias/${id}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/auditorias"] }),
  });
}

export function usePatchItemAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ auditoriaId, atividadeId, data }: {
      auditoriaId: string; atividadeId: string;
      data: {
        observacao?: string | null;
        /** `nota: null` apaga a linha do critério — ausência nunca é nota zero. */
        criterios?: Array<{ audCriterioId: string; nota: number | string | null }>;
      };
    }) => apiRequest("PUT", `/api/auditoria/auditorias/${auditoriaId}/itens/${atividadeId}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/auditorias"] }),
  });
}

export function useFecharAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/auditoria/auditorias/${id}/fechar`).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/auditorias"] }),
  });
}

/** Volta a auditoria fechada pra rascunho e limpa a nota — ao fechar de novo, recalcula. */
export function useReabrirAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/auditoria/auditorias/${id}/reabrir`).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/auditorias"] }),
  });
}

export function useDeleteAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/auditoria/auditorias/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/auditorias"] }),
  });
}

export function useRelatorioFuncionarioAuditoria(de: string, ate: string) {
  return useQuery<RelatorioFuncionario[]>({
    queryKey: ["/api/auditoria/relatorios/funcionario", de, ate],
    queryFn: () => fetchJson<RelatorioFuncionario[]>(`/api/auditoria/relatorios/funcionario?de=${de}&ate=${ate}`),
    enabled: !!de && !!ate,
  });
}

// ─── Faixas — percentual, nunca reais (quem aplica é o Financeiro) ────────────

export function useFaixasAuditoria() {
  return useQuery<AudFaixa[]>({
    queryKey: ["/api/auditoria/faixas"],
    queryFn: () => fetchJson<AudFaixa[]>("/api/auditoria/faixas"),
  });
}

export function useReplaceFaixasAuditoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      vigenciaInicio: string;
      faixas: Array<{ notaMin: string; percentualVariavel: string; percentualFixo: string; rotulo?: string | null }>;
    }) => apiRequest("PUT", "/api/auditoria/faixas", data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/auditoria/faixas"] }),
  });
}

// ─── Resultados — a saída do módulo, em percentual ────────────────────────────

export type ResultadoSetor = {
  setorId: string;
  setorNome: string | null;
  nota: number | null;
  auditorias: number;
  percentualVariavel: number | null;
  percentualFixo: number | null;
};

// ⚠️ REVERSÃO (03/09/2026): a nota da pessoa voltou a ser ÚNICA — o Hugo
// mudou de ideia sobre a decisão do #143 ("remuneração é POR SETOR",
// CONFIRMADA em 2026-08-11). Ver docs/Prompt_Code_Remuneracao.md §2.
// `setores` agora é só MEMÓRIA DE CÁLCULO (a nota que compôs — ou ficou fora
// d)a média), sem percentual próprio: o percentual é da pessoa inteira.
export type ResultadoPessoaSetor = {
  setorId: string;
  setorNome: string | null;
  nota: number | null;
  /** true = fora da média (sem auditoria de fechamento no mês) — nunca conta como zero. */
  foraDaMedia: boolean;
};

export type ResultadoPessoa = {
  funcionarioId: string;
  nome: string;
  /** Média simples das notas dos setores com fechamento no mês — `null` = nenhuma nota. */
  nota: number | null;
  percentualVariavel: number | null;
  percentualFixo: number | null;
  rotulo: string | null;
  setores: ResultadoPessoaSetor[];
};

export type ResultadosAuditoria = {
  competencia: string;
  porSetor: ResultadoSetor[];
  porPessoa: ResultadoPessoa[];
};

export function useResultadosAuditoria(competencia: string | undefined) {
  return useQuery<ResultadosAuditoria>({
    queryKey: ["/api/auditoria/resultados", competencia],
    queryFn: () => fetchJson<ResultadosAuditoria>(`/api/auditoria/resultados?competencia=${competencia}`),
    enabled: !!competencia,
  });
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

// Um ponto POR AUDITORIA fechada, nunca por mês — orientativa e fechamento do
// mesmo setor no mesmo mês são duas rodadas distintas, nunca uma média.
export type DashboardAuditoria = {
  evolucaoPorSetor: Array<{
    auditoriaId: string; setorId: string; setorNome: string | null;
    data: string; tipo: TipoAuditoria; nota: number | null;
  }>;
  evolucaoPorAtividade: Array<{
    atividadeId: string; atividadeNome: string; auditoriaId: string;
    data: string; tipo: TipoAuditoria; nota: number | null; qtd: number;
  }>;
  evolucaoPorResponsavel: Array<{
    funcionarioId: string; nome: string; auditoriaId: string;
    data: string; tipo: TipoAuditoria; nota: number | null;
  }>;
  pioresCriterios: Array<{ criterioId: string; nome: string; atividadeNome: string; nota: number | null; avaliacoes: number }>;
  mapaSetorAtividade: Array<{ setorId: string; setorNome: string | null; atividadeId: string; atividadeNome: string; nota: number | null; qtd: number }>;
  /** `mes` = último mês do período que TEVE auditoria (não necessariamente o fim do período pedido). */
  cobertura: { mes: string; setoresAuditados: number; setoresTotal: number; criteriosAvaliados: number; criteriosPossiveis: number };
};

export function useDashboardAuditoria(de: string | undefined, ate: string | undefined) {
  return useQuery<DashboardAuditoria>({
    queryKey: ["/api/auditoria/dashboard", de, ate],
    queryFn: () => fetchJson<DashboardAuditoria>(`/api/auditoria/dashboard?de=${de}&ate=${ate}`),
    enabled: !!de && !!ate,
  });
}

// ─── Helpers de formatação (usados nas telas) ─────────────────────────────────

export const nota1 = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(1).replace(".", ",");

export const corDaNota = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === "") return "text-muted-foreground";
  const n = Number(v);
  return n >= 8 ? "text-green-700" : n >= 6 ? "text-amber-600" : "text-red-600";
};
